import { type FC, useState } from 'react';
import { Layout } from '../components/Layout';
import { BuyMeCoffee } from '../components/BuyMeCoffee';
import { Footer } from '../components/Footer';
import { Helmet } from 'react-helmet-async';

interface TaxCalculation {
  totalTax: number;
  effectiveTaxRate: number;
  taxBreakdown: {
    slab: string;
    tax: number;
  }[];
  netIncome: number;
}

const CESS_RATE = 0.04;
const SURCHARGE_THRESHOLDS = [5000000, 10000000, 20000000, 50000000];

const formatIndianAmount = (amount: number) =>
  amount.toLocaleString('en-IN', { maximumFractionDigits: 0 });

const formatSlabRange = (start: number, end: number) =>
  end === Infinity
    ? `Above ₹${formatIndianAmount(start)}`
    : `₹${formatIndianAmount(start)} - ₹${formatIndianAmount(end)}`;

const calculateSurcharge = (taxableIncome: number, tax: number) => {
  if (taxableIncome > 20000000) return tax * 0.25;
  if (taxableIncome > 10000000) return tax * 0.15;
  if (taxableIncome > 5000000) return tax * 0.10;
  return 0;
};

const calculateBaseTaxFromSlabs = (
  taxableIncome: number,
  slabs: { limit: number; rate: number }[]
) => {
  let totalTax = 0;
  let remainingIncome = taxableIncome;

  for (let i = 0; i < slabs.length; i++) {
    const slab = slabs[i];
    const prevLimit = i === 0 ? 0 : slabs[i - 1].limit;
    const slabIncome = Math.min(Math.max(0, remainingIncome), slab.limit - prevLimit);

    totalTax += slabIncome * slab.rate;
    remainingIncome -= slabIncome;
    if (remainingIncome <= 0) break;
  }

  return totalTax;
};

const applySurchargeAndCess = (
  taxableIncome: number,
  taxBeforeSurcharge: number,
  taxBreakdown: TaxCalculation['taxBreakdown'],
  slabs: { limit: number; rate: number }[]
) => {
  const surcharge = calculateSurcharge(taxableIncome, taxBeforeSurcharge);
  let surchargeRelief = 0;

  if (surcharge > 0) {
    const applicableThreshold = [...SURCHARGE_THRESHOLDS]
      .reverse()
      .find((threshold) => taxableIncome > threshold);

    if (applicableThreshold) {
      const excessIncome = taxableIncome - applicableThreshold;
      const thresholdBaseTax = calculateBaseTaxFromSlabs(applicableThreshold, slabs);
      const taxAtThreshold = thresholdBaseTax + calculateSurcharge(applicableThreshold, thresholdBaseTax);
      const additionalTax = taxBeforeSurcharge + surcharge - taxAtThreshold;

      if (additionalTax > excessIncome) {
        surchargeRelief = additionalTax - excessIncome;
      }
    }

    taxBreakdown.push({
      slab: 'Surcharge',
      tax: surcharge
    });

    if (surchargeRelief > 0) {
      taxBreakdown.push({
        slab: 'Surcharge Marginal Relief',
        tax: -surchargeRelief
      });
    }
  }

  const taxBeforeCess = Math.max(0, taxBeforeSurcharge + surcharge - surchargeRelief);
  const cess = taxBeforeCess * CESS_RATE;

  if (cess > 0) {
    taxBreakdown.push({
      slab: 'Health & Education Cess (4%)',
      tax: cess
    });
  }

  return taxBeforeCess + cess;
};

const calculateTax2024 = (income: number): TaxCalculation => {
  const standardDeduction = 75000;
  const taxableIncome = Math.max(0, income - standardDeduction);
  const slabs = [
    { limit: 300000, rate: 0 },
    { limit: 700000, rate: 0.05 },
    { limit: 1000000, rate: 0.10 },
    { limit: 1200000, rate: 0.15 },
    { limit: 1500000, rate: 0.20 },
    { limit: Infinity, rate: 0.30 }
  ];
  
  let totalTax = 0;
  let remainingIncome = taxableIncome;
  const taxBreakdown = [];
  
  for (let i = 0; i < slabs.length; i++) {
    const slab = slabs[i];
    const prevLimit = i === 0 ? 0 : slabs[i-1].limit;
    const slabIncome = Math.min(Math.max(0, remainingIncome), slab.limit - prevLimit);
    const slabTax = slabIncome * slab.rate;
    
    if (slabTax > 0) {
      taxBreakdown.push({
        slab: formatSlabRange(prevLimit, slab.limit),
        tax: slabTax
      });
    }
    
    totalTax += slabTax;
    remainingIncome -= slabIncome;
    if (remainingIncome <= 0) break;
  }
  
  // Apply Section 87A rebate and marginal relief using taxable income.
  if (taxableIncome <= 700000) {
    const rebate = Math.min(totalTax, 25000);
    if (rebate > 0) {
      taxBreakdown.push({
        slab: 'Rebate under Section 87A',
        tax: -rebate
      });
    }
    totalTax = Math.max(0, totalTax - rebate);
  } else {
    const excessIncome = taxableIncome - 700000;
    const relief = Math.max(0, totalTax - excessIncome);
    if (relief > 0) {
      taxBreakdown.push({
        slab: 'Marginal Relief',
        tax: -relief
      });
      totalTax -= relief;
    }
  }

  totalTax = applySurchargeAndCess(taxableIncome, totalTax, taxBreakdown, slabs);
  
  return {
    totalTax,
    effectiveTaxRate: income > 0 ? (totalTax / income) * 100 : 0,
    taxBreakdown,
    netIncome: income - totalTax
  };
};

const calculateCurrentTax = (income: number): TaxCalculation => {
  const standardDeduction = 75000;
  const taxableIncome = Math.max(0, income - standardDeduction);
  const slabs = [
    { limit: 400000, rate: 0 },
    { limit: 800000, rate: 0.05 },
    { limit: 1200000, rate: 0.10 },
    { limit: 1600000, rate: 0.15 },
    { limit: 2000000, rate: 0.20 },
    { limit: 2400000, rate: 0.25 },
    { limit: Infinity, rate: 0.30 }
  ];
  
  let totalTax = 0;
  let remainingIncome = taxableIncome;
  const taxBreakdown = [];
  
  for (let i = 0; i < slabs.length; i++) {
    const slab = slabs[i];
    const prevLimit = i === 0 ? 0 : slabs[i-1].limit;
    const slabIncome = Math.min(Math.max(0, remainingIncome), slab.limit - prevLimit);
    const slabTax = slabIncome * slab.rate;
    
    if (slabTax > 0) {
      taxBreakdown.push({
        slab: formatSlabRange(prevLimit, slab.limit),
        tax: slabTax
      });
    }
    
    totalTax += slabTax;
    remainingIncome -= slabIncome;
    if (remainingIncome <= 0) break;
  }
  
  // Apply Section 87A rebate and marginal relief under the current new regime.
  if (taxableIncome <= 1200000) {
    const rebate = Math.min(totalTax, 60000);
    if (rebate > 0) {
      taxBreakdown.push({
        slab: 'Rebate under Section 87A',
        tax: -rebate
      });
    }
    totalTax = Math.max(0, totalTax - rebate);
  } else {
    const excessIncome = taxableIncome - 1200000;
    const relief = Math.max(0, totalTax - excessIncome);
    if (relief > 0) {
      taxBreakdown.push({
        slab: 'Marginal Relief',
        tax: -relief
      });
      totalTax -= relief;
    }
  }

  totalTax = applySurchargeAndCess(taxableIncome, totalTax, taxBreakdown, slabs);
  
  return {
    totalTax,
    effectiveTaxRate: income > 0 ? (totalTax / income) * 100 : 0,
    taxBreakdown,
    netIncome: income - totalTax
  };
};

const TaxRegimeComparison: FC = () => {
  const [income, setIncome] = useState<string>('');
  const [tax2024, setTax2024] = useState<TaxCalculation | null>(null);
  const [currentTax, setCurrentTax] = useState<TaxCalculation | null>(null);

  const formatToIndianNumber = (value: string): string => {
    // Remove existing commas and non-digit characters
    const number = value.replace(/[^\d]/g, '');
    
    if (number === '') return '';
    
    // Convert to Indian numbering system
    const lastThree = number.substring(number.length - 3);
    const otherNumbers = number.substring(0, number.length - 3);
    const formatted = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    return formatted ? `${formatted},${lastThree}` : lastThree;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Format the input value with Indian number system
    const formattedValue = formatToIndianNumber(value);
    setIncome(formattedValue);
  };

  const handleCalculate = () => {
    const incomeValue = parseFloat(income.replace(/,/g, ''));
    if (!isNaN(incomeValue)) {
      setTax2024(calculateTax2024(incomeValue));
      setCurrentTax(calculateCurrentTax(incomeValue));
    }
  };

  const formatCurrency = (amount: number) => {
    const prefix = amount < 0 ? '-₹' : '₹';
    return `${prefix}${Math.abs(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const taxDifference = tax2024 && currentTax ? tax2024.totalTax - currentTax.totalTax : 0;

  return (
    <Layout>
      <Helmet>
        <title>Income Tax Changes 2026-27 | New Tax Regime Comparison</title>
        <meta name="description" content="Compare FY 2024-25 new tax regime rules with the current Tax Year 2026-27 rules. Updated for Budget 2026 continuity, Income-tax Act 2025, slabs, rebate, marginal relief, and cess." />
        <meta name="keywords" content="income tax changes 2026-27, tax year 2026-27, new tax regime comparison, tax calculator india 2026, income tax slabs 2026-27" />
        <link rel="canonical" href="https://www.ctccalculator.in/tax-comparison" />
        <meta name="robots" content="index,follow" />
      </Helmet>
      <div className="container mx-auto px-3 py-4 md:px-4 md:py-8 max-w-7xl">
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            Income Tax Changes for Tax Year 2026-27
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-2">
            Budget 2026 kept the new-regime slabs, rebate, standard deduction, and marginal relief unchanged. Compare FY 2024-25 with the current Tax Year 2026-27 rules.
          </p>
        </div>

        {/* Tax Calculator Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 mb-8 md:mb-12 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Tax Calculator
          </h2>
          <div className="flex flex-col gap-4 md:flex-row md:gap-6 md:items-end mb-6 md:mb-8">
            <div className="flex-1">
              <label htmlFor="income" className="block text-sm font-semibold text-gray-700 mb-2">
                Annual Income (₹)
              </label>
              <input
                type="text"
                id="income"
                value={income}
                onChange={handleInputChange}
                placeholder="Enter your annual income"
                className="w-full px-3 py-2 md:px-4 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base"
                inputMode="numeric"
                pattern="[0-9,]*"
              />
            </div>
            <button
              onClick={handleCalculate}
              className="w-full md:w-auto px-6 md:px-8 py-2 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-semibold shadow-sm text-base"
            >
              Calculate Tax
            </button>
          </div>

          {tax2024 && currentTax && (
            <div className="space-y-6 md:space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {/* 2024-25 Results */}
                <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-gray-200">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 pb-2 border-b border-gray-200">
                    FY 2024-25 Tax Calculation
                  </h3>
                  <div className="space-y-3 md:space-y-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Tax:</span>
                      <span className="text-lg md:text-xl font-semibold">{formatCurrency(tax2024.totalTax)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Effective Tax Rate:</span>
                      <span className="text-lg md:text-xl font-semibold">{tax2024.effectiveTaxRate.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Net Income:</span>
                      <span className="text-lg md:text-xl font-semibold text-green-600">{formatCurrency(tax2024.netIncome)}</span>
                    </div>
                    {tax2024.taxBreakdown.length > 0 && (
                      <div className="mt-4 md:mt-6">
                        <p className="font-semibold text-gray-900 mb-2">Tax Breakdown:</p>
                        <div className="bg-white rounded-lg p-3 md:p-4 border border-gray-200 overflow-x-auto">
                          {tax2024.taxBreakdown.map((item, index) => (
                            <div key={index} className="flex justify-between items-center py-1">
                              <span className="text-sm md:text-base text-gray-600">{item.slab}</span>
                              <span className="font-medium">{formatCurrency(item.tax)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Current Tax Year Results */}
                <div className="bg-blue-50 rounded-xl p-4 md:p-6 border border-blue-200">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 pb-2 border-b border-blue-200">
                    Current Tax Year 2026-27 Calculation
                  </h3>
                  <div className="space-y-3 md:space-y-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Tax:</span>
                      <span className="text-lg md:text-xl font-semibold">{formatCurrency(currentTax.totalTax)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Effective Tax Rate:</span>
                      <span className="text-lg md:text-xl font-semibold">{currentTax.effectiveTaxRate.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Net Income:</span>
                      <span className="text-lg md:text-xl font-semibold text-green-600">{formatCurrency(currentTax.netIncome)}</span>
                    </div>
                    {currentTax.taxBreakdown.length > 0 && (
                      <div className="mt-4 md:mt-6">
                        <p className="font-semibold text-gray-900 mb-2">Tax Breakdown:</p>
                        <div className="bg-white rounded-lg p-3 md:p-4 border border-blue-200 overflow-x-auto">
                          {currentTax.taxBreakdown.map((item, index) => (
                            <div key={index} className="flex justify-between items-center py-1">
                              <span className="text-sm md:text-base text-gray-600">{item.slab}</span>
                              <span className="font-medium">{formatCurrency(item.tax)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tax Savings */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 md:p-6 border border-green-200">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-6">Your Tax Savings</h3>
                <p className="text-lg md:text-xl">
                  Compared with FY 2024-25 new-regime rules, the current rules make you{' '}
                  <span className={taxDifference >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {taxDifference >= 0 ? 'save' : 'pay extra'}{' '}
                    {formatCurrency(Math.abs(taxDifference))}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tax Slabs Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* FY 2024-25 */}
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 border border-gray-100">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">FY 2024-25</h2>
            <div className="space-y-4 md:space-y-6">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Tax Slabs</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <div className="min-w-full">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm md:text-base font-semibold text-gray-900">Income Range (₹)</th>
                          <th className="px-4 py-3 text-left text-sm md:text-base font-semibold text-gray-900">Tax Rate</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 text-sm md:text-base">
                        <tr><td className="px-4 py-3">Up to 3,00,000</td><td className="px-4 py-3">Nil</td></tr>
                        <tr><td className="px-4 py-3">3,00,001 - 7,00,000</td><td className="px-4 py-3">5%</td></tr>
                        <tr><td className="px-4 py-3">7,00,001 - 10,00,000</td><td className="px-4 py-3">10%</td></tr>
                        <tr><td className="px-4 py-3">10,00,001 - 12,00,000</td><td className="px-4 py-3">15%</td></tr>
                        <tr><td className="px-4 py-3">12,00,001 - 15,00,000</td><td className="px-4 py-3">20%</td></tr>
                        <tr><td className="px-4 py-3">Above 15,00,000</td><td className="px-4 py-3">30%</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Key Features</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Standard Deduction: ₹75,000
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Rebate up to ₹7,00,000 taxable income
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Marginal relief above ₹7L taxable income
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Salaried zero-tax income: ₹7,75,000
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Current Tax Year */}
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 border border-gray-100">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Tax Year 2026-27</h2>
            <div className="space-y-4 md:space-y-6">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Tax Slabs</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <div className="min-w-full">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm md:text-base font-semibold text-gray-900">Income Range (₹)</th>
                          <th className="px-4 py-3 text-left text-sm md:text-base font-semibold text-gray-900">Tax Rate</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 text-sm md:text-base">
                        <tr><td className="px-4 py-3">Up to 4,00,000</td><td className="px-4 py-3">Nil</td></tr>
                        <tr><td className="px-4 py-3">4,00,001 - 8,00,000</td><td className="px-4 py-3">5%</td></tr>
                        <tr><td className="px-4 py-3">8,00,001 - 12,00,000</td><td className="px-4 py-3">10%</td></tr>
                        <tr><td className="px-4 py-3">12,00,001 - 16,00,000</td><td className="px-4 py-3">15%</td></tr>
                        <tr><td className="px-4 py-3">16,00,001 - 20,00,000</td><td className="px-4 py-3">20%</td></tr>
                        <tr><td className="px-4 py-3">20,00,001 - 24,00,000</td><td className="px-4 py-3">25%</td></tr>
                        <tr><td className="px-4 py-3">Above 24,00,000</td><td className="px-4 py-3">30%</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Key Features</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Standard Deduction: ₹75,000
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Rebate up to ₹12,00,000 taxable income
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Marginal relief above ₹12L taxable income
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Salaried zero-tax income: ₹12,75,000
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Key Changes Section */}
          <div className="md:col-span-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg p-4 md:p-8 border border-blue-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              What Changed This Year
            </h2>
            <ul className="grid md:grid-cols-2 gap-4">
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Budget 2026 made no fresh slab, rebate, or standard-deduction change</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Income-tax Act, 2025 applies from April 1, 2026 for Tax Year 2026-27</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Current rules carry forward the ₹4L nil slab and 5%, 10%, 15%, 20%, 25%, 30% slab structure</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Section 87A rebate continues up to ₹12L taxable income under the new regime</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Marginal relief continues for taxable income slightly above ₹12L, with cess calculated after relief</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>FY 2024-25 comparison uses the July 2024 slab revision and ₹75,000 standard deduction</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center">
        <BuyMeCoffee />
      </div>
      
      <Footer />
    </Layout>
  );
};

export default TaxRegimeComparison;
